<script setup lang="ts">
import {
	PropType,
	computed,
	onBeforeMount,
	ref,
	shallowRef,
	toRefs,
} from "vue";
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import DimensionDO = App.Data.DimensionDO;

const { t } = useI18n();

const props = defineProps({
	role: {
		type: Object as PropType<App.Data.RoleDO>,
		require: true,
	},
});

const { role } = toRefs(props);

// Emit is not used in this component but required through the tabs-router-view setup.
const emit = defineEmits(["update-role"]);

const uuid: string | string[] = useRoute().params.uuid ?? "";

const isLoading = shallowRef(true);
const isSaving = shallowRef(false);
const favoriteDimensions = ref<DimensionDO[]>([]);
const allDimensions = ref<DimensionDO[]>([]);
const selectedDimensionNameClean = ref<string | null>(null);

const selectableDimensions = computed(() =>
	allDimensions.value.filter(
		(dimension) =>
			!favoriteDimensions.value.some(
				(favorite) => favorite.name_clean === dimension.name_clean
			)
	)
);

const dimensionTitle = (dimension: DimensionDO) =>
	t(`properties.${dimension.name_clean}.title`);

const loadDimensions = async () => {
	isLoading.value = true;

	const [favoritesResponse, allDimensionsResponse] = await Promise.all([
		useApiClient("roles").getRoleFavoriteDimensions(uuid),
		useApiClient("dimensions").get(),
	]);

	if (favoritesResponse.isOk()) {
		favoriteDimensions.value = favoritesResponse.value;
	}

	if (allDimensionsResponse.isOk()) {
		allDimensions.value = allDimensionsResponse.value;
	}

	isLoading.value = false;
};

const addDimension = async () => {
	const dimension = selectableDimensions.value.find(
		(d) => d.name_clean === selectedDimensionNameClean.value
	);
	if (!dimension) return;

	isSaving.value = true;

	const response = await toastApiResult(
		useApiClient("roles").addRoleFavoriteDimension(uuid, dimension)
	);

	if (response.isOk()) {
		favoriteDimensions.value.push(dimension);
		selectedDimensionNameClean.value = null;
	}

	isSaving.value = false;
};

const removeDimension = async (dimension: DimensionDO) => {
	const confirmed = await useDialog().confirm({
		title: t("roles.detail.favoriteDimensions.removeTitle"),
		message: t("roles.detail.favoriteDimensions.removeConfirmation", {
			name: dimensionTitle(dimension),
		}),
		isDelete: true,
	});
	if (!confirmed) return;

	const response = await toastApiResult(
		useApiClient("roles").removeRoleFavoriteDimension(uuid, dimension)
	);

	if (response.isOk()) {
		favoriteDimensions.value = favoriteDimensions.value.filter(
			(favorite) => favorite.name_clean !== dimension.name_clean
		);
	}
};

onBeforeMount(loadDimensions);
</script>

<template>
	<module-loading>
		<v-card class="mt-5 pa-5">
			<div class="d-flex ga-2 align-center">
				<v-autocomplete
					:label="t('roles.detail.favoriteDimensions.selectPlaceholder')"
					:items="selectableDimensions"
					:item-title="dimensionTitle"
					item-value="name_clean"
					v-model="selectedDimensionNameClean"
					:loading="isLoading"
					clearable
					hide-details
				/>
				<v-btn
					color="primary"
					:disabled="!selectedDimensionNameClean || isSaving"
					@click="addDimension"
				>
					{{ t("roles.detail.favoriteDimensions.actionAdd") }}
				</v-btn>
			</div>

			<v-divider class="my-4" />

			<v-list v-if="favoriteDimensions.length" density="compact">
				<v-list-item
					v-for="dimension in favoriteDimensions"
					:key="dimension.name_clean"
				>
					<template v-slot:append>
						<v-btn
							icon
							variant="text"
							size="small"
							color="error"
							@click="removeDimension(dimension)"
						>
							<i class="fa-light fa-trash-can"></i>
						</v-btn>
					</template>
					{{ dimensionTitle(dimension) }}
				</v-list-item>
			</v-list>
			<div v-else class="text-medium-emphasis pt-2">
				{{ t("roles.detail.favoriteDimensions.empty") }}
			</div>
		</v-card>
	</module-loading>
</template>

<style scoped lang="scss"></style>
